import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LoadingService } from '@services/loading.service';
import { LoggerService } from '@services/logger.service';
import { ToastService } from '@services/toast.service';
import { formatApiErrorMessage } from '@shared/utils/api-response.utils';

import type { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(LoggerService);
  const toastService = inject(ToastService);
  const loadingService = inject(LoadingService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'Si è verificato un errore imprevisto.';

      if (error.error instanceof ErrorEvent) {
        // Client-side error (e.g. network issue)
        errorMessage = `Errore di rete: ${error.error.message}`;
        logger.error('Client-side error:', error.error.message);
      } else {
        // Server-side error
        const serverError = error.error;
        const status = error.status;

        logger.error(`Server error (Status ${status}):`, serverError);

        const backendMessage = serverError?.message || serverError?.Message;
        const hasErrors =
          Array.isArray(serverError?.errors) ||
          (serverError?.errors && typeof serverError.errors === 'object');

        if ((backendMessage && typeof backendMessage === 'string') || hasErrors) {
          errorMessage = formatApiErrorMessage(serverError, 'Errore del server.');
        } else if (status === 0) {
          errorMessage = 'Impossibile connettersi al server. Controlla la tua connessione.';
        } else if (status === 401) {
          // 401 is usually handled by AuthInterceptor/Guard, but we can set a state here
          errorMessage = 'Sessione scaduta. Per favore, effettua di nuovo il login.';
        } else if (status === 403) {
          errorMessage = 'Non hai i permessi per eseguire questa operazione.';
        } else if (status === 404) {
          errorMessage = 'Risorsa non trovata.';
        } else if (status >= 500) {
          errorMessage = 'Errore interno del server. Riprova più tardi.';
        } else {
          errorMessage = formatApiErrorMessage(
            serverError,
            (typeof serverError?.error === 'string' && serverError.error) ||
              'Errore del server.',
          );
        }
      }

      // Update global error state in LoadingService
      loadingService.setError(errorMessage);

      // Show toast notification (centralized)
      // Note: We skip 401 here if it's already handled by authInterceptor to avoid double toasts
      if (error.status !== 401) {
        toastService.error(errorMessage);
      }

      return throwError(() => error);
    })
  );
};
